/**
 * Approval Workflows admin page
 *
 * Configure who must approve content artifacts (communication templates,
 * media assets, workflow templates) before they can be used in regulated
 * mode. Each workflow has a scope and an ordered list of steps; each step
 * specifies a role and how many approvers from that role are required.
 *
 * The compliance section lists pending requests; this page lists the
 * workflow *definitions* that drive those requests.
 */

import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ArrowLeft,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Users,
  GitBranch,
  Info,
  X,
} from 'lucide-react';
import {
  approvalsAPI,
  ApprovalWorkflow,
  SubjectKind,
} from '@/services/api/approvals';
import { useComplianceMode } from '@/hooks/useComplianceMode';

interface Props {
  /** When set, renders a back link instead of standalone-page header. */
  onBack?: () => void;
  /** Override the hook-derived isRegulated value (parent has the authoritative one from /api/compliance/settings). */
  isRegulatedOverride?: boolean;
}

const SCOPE_LABELS: Record<SubjectKind, string> = {
  communication_template: 'Communication templates',
  media_asset: 'Media assets',
  workflow_template: 'Workflow templates',
  doc_type: 'Document types',
  campaign: 'Campaigns',
  content_asset: 'Content Studio assets',
};

const ROLES = [
  'super_admin',
  'org_admin',
  'compliance_reviewer',
  'supervisor',
  'program_manager',
  'staff',
] as const;

const DEFAULT_NEW_STEP = { order: 1, name: 'Compliance review', role: 'compliance_reviewer', approvers_n: 1 };

interface FormState {
  id?: number;
  name: string;
  description: string;
  scope: SubjectKind;
  steps: Array<{ order: number; name?: string; role: string; approvers_n: number }>;
  is_default: boolean;
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  scope: 'communication_template',
  steps: [{ ...DEFAULT_NEW_STEP }],
  is_default: false,
  is_active: true,
};

export default function ApprovalWorkflowsPage({ onBack, isRegulatedOverride }: Props = {}) {
  const { isRegulated: hookRegulated, canManageCompliance } = useComplianceMode();
  const isRegulated = isRegulatedOverride ?? hookRegulated;
  const [workflows, setWorkflows] = useState<ApprovalWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ApprovalWorkflow | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setWorkflows(await approvalsAPI.listWorkflows());
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, steps: [{ ...DEFAULT_NEW_STEP }] });
    setEditing(null);
    setCreating(true);
  };

  const openEdit = (wf: ApprovalWorkflow) => {
    setForm({
      id: wf.id,
      name: wf.name,
      description: wf.description || '',
      scope: wf.scope,
      steps: (wf.steps && wf.steps.length > 0
        ? wf.steps
        : [{ ...DEFAULT_NEW_STEP }]) as FormState['steps'],
      is_default: wf.is_default,
      is_active: wf.is_active,
    });
    setEditing(wf);
    setCreating(true);
  };

  const closeModal = () => {
    if (saving) return;
    setCreating(false);
    setEditing(null);
  };

  const addStep = () =>
    setForm((f) => ({
      ...f,
      steps: [
        ...f.steps,
        {
          order: f.steps.length + 1,
          name: 'Additional review',
          role: 'org_admin',
          approvers_n: 1,
        },
      ],
    }));

  const removeStep = (idx: number) =>
    setForm((f) => ({
      ...f,
      steps: f.steps
        .filter((_, i) => i !== idx)
        .map((s, i) => ({ ...s, order: i + 1 })),
    }));

  const updateStep = (idx: number, patch: Partial<FormState['steps'][number]>) =>
    setForm((f) => ({
      ...f,
      steps: f.steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // Backend doesn't expose a PATCH for workflows in v1; on edit we soft-delete
      // the old definition and create a new one with the same fields.
      if (editing) {
        await approvalsAPI.deleteWorkflow(editing.id);
      }
      await approvalsAPI.createWorkflow({
        name: form.name,
        description: form.description,
        scope: form.scope,
        scope_filter: {},
        steps: form.steps,
        is_default: form.is_default,
        is_active: form.is_active,
      });
      closeModal();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (wf: ApprovalWorkflow) => {
    if (!confirm(`Disable workflow "${wf.name}"? Pending requests are unaffected.`)) return;
    setDeletingId(wf.id);
    try {
      await approvalsAPI.deleteWorkflow(wf.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setDeletingId(null);
    }
  };

  if (!canManageCompliance) {
    return (
      <div className="container mx-auto p-8 max-w-3xl">
        <Card>
          <CardContent className="py-10 text-center text-sm text-ink-500">
            You don't have permission to configure approval workflows.
          </CardContent>
        </Card>
      </div>
    );
  }

  const grouped: Record<string, ApprovalWorkflow[]> = {};
  for (const wf of workflows) {
    grouped[wf.scope] = [...(grouped[wf.scope] || []), wf];
  }

  return (
    <div className={onBack ? 'space-y-6' : 'container mx-auto py-8 px-4 max-w-6xl space-y-6'}>
      {onBack && (
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-sm text-ink-500 hover:text-ink-700 mb-2 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Compliance Settings
        </button>
      )}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
            <GitBranch className="h-6 w-6" /> Approval Workflows
          </h1>
          <p className="text-sm text-ink-500 mt-1 max-w-2xl">
            Define who must approve content artifacts before they go live. Each workflow has a
            scope (templates / media / workflow templates) and ordered review steps. Mark one
            workflow per scope as <strong>default</strong> — it's used for any artifact that doesn't
            match a more specific filter.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-1" />
          New workflow
        </Button>
      </div>

      {!isRegulated && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            This org is in <strong>Standard mode</strong> — workflows are still recorded, but the
            send-time gate doesn't enforce them. Switch to Regulated Entity mode in Org Settings →
            Compliance for enforcement.
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-ink-500">
            <Loader2 className="h-4 w-4 inline animate-spin mr-2" />
            Loading workflows…
          </CardContent>
        </Card>
      ) : workflows.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-ink-500">
            No approval workflows defined yet. Click <strong>New workflow</strong> to create one for
            communication templates or media assets.
          </CardContent>
        </Card>
      ) : (
        Object.entries(grouped).map(([scope, list]) => (
          <Card key={scope}>
            <CardHeader>
              <CardTitle className="text-base">
                {SCOPE_LABELS[scope as SubjectKind] || scope}{' '}
                <span className="text-xs font-normal text-ink-500">
                  ({list.length} {list.length === 1 ? 'workflow' : 'workflows'})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-paper text-left text-xs uppercase">
                  <tr>
                    <th className="p-3">Name</th>
                    <th className="p-3">Steps</th>
                    <th className="p-3">Default</th>
                    <th className="p-3">Active</th>
                    <th className="p-3 w-32"></th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((wf) => (
                    <tr key={wf.id} className="border-t">
                      <td className="p-3">
                        <div className="font-medium">{wf.name}</div>
                        {wf.description && (
                          <div className="text-xs text-ink-500">{wf.description}</div>
                        )}
                      </td>
                      <td className="p-3">
                        <ol className="space-y-1">
                          {(wf.steps || []).map((s, i) => (
                            <li key={i} className="text-xs flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                Step {s.order ?? i + 1}
                              </Badge>
                              <Users className="h-3 w-3 text-ink-400" />
                              <code className="px-1 bg-paper-2 rounded">
                                {s.role}
                              </code>
                              {s.approvers_n ? (
                                <span className="text-ink-500">×{s.approvers_n}</span>
                              ) : null}
                              {s.name && <span className="text-ink-500">— {s.name}</span>}
                            </li>
                          ))}
                        </ol>
                      </td>
                      <td className="p-3">
                        {wf.is_default ? (
                          <Badge className="bg-amber-100 text-amber-900">Default</Badge>
                        ) : (
                          <span className="text-ink-400">No</span>
                        )}
                      </td>
                      <td className="p-3">
                        {wf.is_active ? (
                          <Badge className="bg-green-100 text-green-900">Active</Badge>
                        ) : (
                          <Badge variant="outline">Inactive</Badge>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(wf)}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={deletingId === wf.id}
                          onClick={() => remove(wf)}
                        >
                          {deletingId === wf.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Trash2 className="h-3 w-3" />
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        ))
      )}

      <Dialog open={creating} onOpenChange={(o) => !o && closeModal()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit workflow' : 'New approval workflow'}</DialogTitle>
            <DialogDescription>
              When a user submits a {(SCOPE_LABELS[form.scope] || form.scope || 'item').toString().toLowerCase().replace(/s$/, '')} for
              review, the request goes through these steps in order. Each step is satisfied when
              the required number of approvers from the named role decide.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Name</Label>
                <Input
                  value={form.name}
                  placeholder="Compliance Review (Bulk Email)"
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-sm">Scope</Label>
                <Select
                  value={form.scope}
                  onValueChange={(v: any) => setForm((f) => ({ ...f, scope: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SCOPE_LABELS).map(([k, label]) => (
                      <SelectItem key={k} value={k}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-sm">Description (optional)</Label>
              <Input
                value={form.description}
                placeholder="Single-step review by compliance team"
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm">Review steps</Label>
                <Button size="sm" variant="outline" onClick={addStep}>
                  <Plus className="h-3 w-3 mr-1" /> Add step
                </Button>
              </div>
              <div className="space-y-3">
                {form.steps.map((s, idx) => (
                  <div
                    key={idx}
                    className="rounded-md border p-3 space-y-2 bg-paper"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-ink-500">Step {s.order ?? idx + 1}</span>
                      {form.steps.length > 1 && (
                        <button
                          onClick={() => removeStep(idx)}
                          className="text-ink-400 hover:text-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs">Step name</Label>
                        <Input
                          value={s.name || ''}
                          placeholder="Compliance review"
                          onChange={(e) => updateStep(idx, { name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Approver role</Label>
                        <Select
                          value={s.role}
                          onValueChange={(v) => updateStep(idx, { role: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ROLES.map((r) => (
                              <SelectItem key={r} value={r}>
                                {r}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs"># required</Label>
                        <Input
                          type="number"
                          min={1}
                          value={s.approvers_n}
                          onChange={(e) =>
                            updateStep(idx, { approvers_n: Math.max(1, Number(e.target.value)) })
                          }
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label className="text-sm font-medium">Default for this scope</Label>
                  <p className="text-xs text-ink-500 mt-1">
                    Used when no more-specific filter matches.
                  </p>
                </div>
                <Switch
                  checked={form.is_default}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, is_default: v }))}
                />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label className="text-sm font-medium">Active</Label>
                  <p className="text-xs text-ink-500 mt-1">
                    Disable to keep the definition without applying it.
                  </p>
                </div>
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeModal} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving || !form.name.trim() || form.steps.length === 0}>
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              {editing ? 'Save changes' : 'Create workflow'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
